import type { CalendarApi } from "@fullcalendar/core";

/**
 * FullCalendar swaps the visible range instantly on prev/next. This makes it *glide*:
 * snapshot the outgoing view as a ghost, advance the real calendar underneath it, then
 * carousel the two — the ghost slides out one way while the fresh range slides in from
 * the other. Relies on the {@code scheda-glide-*} keyframes in the shared calendar theme.
 *
 * @param api        the FullCalendar API
 * @param container  the element wrapping the calendar (holds the .fc-view-harness)
 * @param dir        "next" or "prev"
 */
const GLIDE_MS = 700;

export function glideCalendar(
  api: CalendarApi,
  container: HTMLElement | null,
  dir: "next" | "prev",
): void {
  const harness = container?.querySelector(".fc-view-harness") as HTMLElement | null;
  const parent = harness?.parentElement as HTMLElement | null;
  if (!harness || !parent) {
    api[dir]();
    return;
  }
  const ghost = harness.cloneNode(true) as HTMLElement;
  const prevPos = parent.style.position;
  const prevOverflow = parent.style.overflow;
  parent.style.position = "relative";
  // Clip the outgoing ghost + incoming view to the calendar so they don't slide out
  // over the rest of the page as they cross.
  parent.style.overflow = "hidden";
  Object.assign(ghost.style, {
    position: "absolute",
    top: `${harness.offsetTop}px`,
    left: `${harness.offsetLeft}px`,
    width: `${harness.offsetWidth}px`,
    height: `${harness.offsetHeight}px`,
    margin: "0",
    pointerEvents: "none",
    zIndex: "5",
  });
  parent.appendChild(ghost);

  api[dir](); // the fresh range renders instantly beneath the ghost

  ghost.classList.add(dir === "next" ? "scheda-glide-out-left" : "scheda-glide-out-right");
  harness.classList.remove("scheda-glide-in-right", "scheda-glide-in-left");
  void harness.offsetWidth; // reflow so the in-animation restarts cleanly
  harness.classList.add(dir === "next" ? "scheda-glide-in-right" : "scheda-glide-in-left");

  window.setTimeout(() => {
    ghost.remove();
    harness.classList.remove("scheda-glide-in-right", "scheda-glide-in-left");
    parent.style.position = prevPos;
    parent.style.overflow = prevOverflow;
  }, GLIDE_MS + 40);
}
